import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const CommissionSummary = ({
  data,
  onFilterChange,
  selectedTimeRange,
  selectedTerritory,
  selectedTransactionType
}) => {
  const timeRangeOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' }
  ];

  const territoryOptions = [
    { value: 'all', label: 'All Territories' },
    { value: 'north-america', label: 'North America' },
    { value: 'europe', label: 'Europe' },
    { value: 'asia-pacific', label: 'Asia Pacific' },
    { value: 'latin-america', label: 'Latin America' }
  ];

  const transactionTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'international', label: 'International Transfer' },
    { value: 'domestic', label: 'Domestic Transfer' },
    { value: 'business', label: 'Business Transfer' }
  ];

  const summaryCards = [
    {
      title: 'Current Balance',
      value: `$${data?.currentBalance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      change: '+8.2%',
      changeType: 'positive',
      icon: 'Wallet',
      iconColor: 'bg-success'
    },
    {
      title: 'Pending Payouts',
      value: `$${data?.pendingPayouts?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      change: '+12.5%',
      changeType: 'positive',
      icon: 'Clock',
      iconColor: 'bg-warning'
    },
    {
      title: 'This Month',
      value: `$${data?.thisMonthEarnings?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      change: '+15.3%',
      changeType: 'positive',
      icon: 'TrendingUp',
      iconColor: 'bg-primary'
    },
    {
      title: 'Total Earnings',
      value: `$${data?.totalEarnings?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      change: '+22.7%',
      changeType: 'positive',
      icon: 'DollarSign',
      iconColor: 'bg-accent'
    }
  ];

  const getChangeColor = (changeType) => {
    if (changeType === 'positive') return 'text-success';
    if (changeType === 'negative') return 'text-error';
    return 'text-muted-foreground';
  };

  const getChangeIcon = (changeType) => {
    if (changeType === 'positive') return 'TrendingUp';
    if (changeType === 'negative') return 'TrendingDown';
    return 'Minus';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <label className="block text-sm font-medium text-foreground mb-2">
            Time Range
          </label>
          <Select
            value={selectedTimeRange}
            onValueChange={(value) => onFilterChange('timeRange', value)}
            options={timeRangeOptions}
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-foreground mb-2">
            Territory
          </label>
          <Select
            value={selectedTerritory}
            onValueChange={(value) => onFilterChange('territory', value)}
            options={territoryOptions}
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-foreground mb-2">
            Transaction Type
          </label>
          <Select
            value={selectedTransactionType}
            onValueChange={(value) => onFilterChange('transactionType', value)}
            options={transactionTypeOptions}
          />
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {summaryCards?.map((card, index) => (
          <div key={index} className="bg-background border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card?.iconColor}`}>
                  <Icon name={card?.icon} size={20} color="white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{card?.title}</p>
                  <p className="text-xl font-bold text-foreground">{card?.value}</p>
                </div>
              </div>
              {card?.change && (
                <div className={`flex items-center space-x-1 ${getChangeColor(card?.changeType)}`}>
                  <Icon name={getChangeIcon(card?.changeType)} size={16} />
                  <span className="text-sm font-medium">{card?.change}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Quick Stats */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-sm text-muted-foreground">Commission Rate</p>
            <p className="text-lg font-semibold text-foreground">{data?.commissionRate}%</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Last Payout</p>
            <p className="text-lg font-semibold text-foreground">
              {new Date(data?.lastPayoutDate)?.toLocaleDateString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Next Payout</p>
            <p className="text-lg font-semibold text-foreground">
              {new Date(data?.nextPayoutDate)?.toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionSummary;